import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, ShieldCheck, FileText, Lock } from "lucide-react";
import { COLORS } from "@/lib/api";

const fade = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

const BENEFICIOS = [
  { icon: ShieldCheck, t: "Donataria autorizada", d: "Tus donativos son deducibles de impuestos ante el SAT." },
  { icon: Lock, t: "Pago 100% seguro", d: "El cobro se procesa de forma segura y encriptada a través de Stripe." },
  { icon: FileText, t: "Transparencia total", d: "Rendimos cuentas de cada aporte en nuestros informes de resultados." },
];

const DonaAhora = () => {
  useEffect(() => {
    const SRC = "https://js.stripe.com/v3/buy-button.js";
    if (!document.querySelector(`script[src="${SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <div data-testid="subpage-dona-ahora">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]" style={{ background: `linear-gradient(120deg, ${COLORS.magenta} 0%, ${COLORS.orange} 100%)` }}>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link to="/donaciones" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Donaciones
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6 bg-white" />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Dona Ahora
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 font-medium max-w-2xl">
            Tu aporte se transforma en liderazgos que cambian a México. Con tu donación sostenemos talleres,
            materiales y acompañamiento para las juventudes.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Tarjeta principal de donación */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="rounded-3xl border border-[#E5E5E5] overflow-hidden">
          <div className="p-8 md:p-10 text-center" style={{ background: "#FDE7FA" }}>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white mb-5">
              <Heart size={30} className="text-brand-magenta" fill={COLORS.magenta} />
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">
              Haz tu donativo en segundos
            </h2>
            <p className="mt-3 text-brand-ink/75 max-w-xl mx-auto">
              Elige el monto que desees aportar y completa tu donativo de forma segura. Cada peso cuenta para
              impulsar a más juventudes.
            </p>
            <div className="mt-7 flex justify-center" data-testid="donate-button">
              <stripe-buy-button
                buy-button-id="buy_btn_1Twl8XBN0hyNRZU7gR1UrmdY"
                publishable-key="pk_live_51TwVIjBN0hyNRZU7UapgLRLInPhpuQ1F2frT9ifMfVfz1w6WDQ9K2OYA6SM39eF4hAQALZuGUYLoZ1yKgf34lD8o00vwZlneqx"
              ></stripe-buy-button>
            </div>
            <p className="mt-4 text-xs text-brand-ink/50 flex items-center justify-center gap-1.5">
              <Lock size={12} /> Pago seguro procesado por Stripe
            </p>
          </div>
        </motion.div>

        {/* Beneficios */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {BENEFICIOS.map((b) => (
            <motion.div key={b.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="border border-[#E5E5E5] rounded-3xl p-6">
              <b.icon size={24} className="text-brand-magenta" />
              <p className="font-heading text-lg font-bold text-brand-ink mt-3">{b.t}</p>
              <p className="text-sm text-brand-ink/70 mt-1">{b.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Otras formas */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="mt-10 rounded-3xl bg-brand-ink p-8 text-center">
          <p className="text-white/90">
            ¿Quieres donar por otro medio o hacer una aportación institucional? Escríbenos a{" "}
            <a href="mailto:comunidad@latentes.mx" className="font-semibold text-brand-magenta hover:underline">comunidad@latentes.mx</a>
          </p>
        </motion.div>

        {/* Transparencia */}
        <Link to="/donaciones/informes-de-resultados" data-testid="informes-cta" className="mt-8 group flex items-center justify-between gap-4 bg-brand-orange rounded-3xl p-7 hover:bg-brand-magenta transition-colors">
          <div>
            <h3 className="font-heading text-xl font-semibold tracking-tight text-white">Conoce el impacto de tu apoyo</h3>
            <p className="mt-1 text-sm text-white/80">Consulta nuestros informes de resultados.</p>
          </div>
          <ArrowLeft size={24} className="text-white flex-shrink-0 rotate-180 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default DonaAhora;
